import { exactCurrencyFormatter, useFetch } from "@/utils"
import { format, parseISO } from "date-fns"
import { Button } from "@/components/ui/button"
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react"
import { api_fetch_throws, CheckDetail } from "@/api"
import { RefreshFlag } from "@/refresh_flag.tsx"
import useLocation from "wouter-preact/use-location"
import { check_api_path } from "./check_path"

interface CheckCodingHeaderProps {
  number?: string
  transaction_id: string
  class?: string
}

export function CheckCodingHeader(props: CheckCodingHeaderProps) {
  let { number } = props
  let [, setLocation] = useLocation()
  let statusFlag = RefreshFlag.use()
  let { data: check } = useFetch<CheckDetail | undefined>(
    () => (number ? api_fetch_throws(check_api_path(number)) : Promise.resolve(undefined)),
    [statusFlag, number]
  )

  // Don't show anything when there's no check number
  if (!number) {
    return null
  }

  // Show skeleton only when data is loading
  if (!check) {
    return (
      <div class="w-full">
        <div class="flex items-center justify-between space-x-4">
          <div class="flex flex-col items-end">
            <div class="h-4 w-20 bg-gray-200 rounded animate-pulse mb-1"></div>
            <div class="h-8 w-24 bg-gray-200 rounded animate-pulse mb-1"></div>
            <div class="h-4 w-20 bg-gray-200 rounded animate-pulse mb-1"></div>
            <div class="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
          </div>

          {/* Navigation Controls Skeleton */}
          <div class="flex items-center gap-2">
            <div class="text-center mx-2">
              <div class="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  let date = check.date ? format(parseISO(check.date), "PP") : undefined

  // Find current transaction index
  let currentIndex = check.transactions.findIndex(t => t.id === props.transaction_id)

  // Navigation logic
  let skip_previous
  let previous = currentIndex > 0 ? check.transactions[currentIndex - 1].id : undefined
  let next = currentIndex < check.transactions.length - 1 ? check.transactions[currentIndex + 1].id : undefined
  let skip_next

  // // Find previous transaction needing coding
  // for (let i = currentIndex - 1; i >= 0; i--) {
  //   if (!check.transactions[i].coded) {
  //     skip_previous = check.transactions[i].id
  //     break
  //   }
  // }

  // // Find next transaction needing coding
  // for (let i = currentIndex + 1; i < check.transactions.length; i++) {
  //   if (!check.transactions[i].coded) {
  //     skip_next = check.transactions[i].id
  //     break
  //   }
  // }

  // Calculate transaction counts
  let totalTransactions = check.transactions.length
  let codedCount = check.transactions.filter(t => t.coded).length

  // Calculate total deductions amount
  let totalDeductions = check.transactions
    .filter(t => t.kind === 'deduction')
    .reduce((sum, t) => sum + Math.abs(t.invoice_amount), 0)

  let error
  if (check.count_hint && check.count_hint !== totalTransactions) {
    // If count_hint is provided, use it instead of the actual count
    error = (
      <div class="text-red-400 text-sm mt-1">
        Warning: The parsed check has {check.count_hint} rows, but there are {totalTransactions} transactions.
      </div>
    )
  }
  return (
    <div class="w-full">
      <div class="flex items-center justify-between space-x-4">
        <div class="flex flex-col items-end">
          <p class="text-sm text-gray-500">{date}</p>
          <h2 class="text-lg text-gray-900">Check #{number}</h2>
          <div class="text-sm text-gray-600">
            <div>Check Amount: {check.amount != null ? exactCurrencyFormatter(check.amount) : 'N/A'}</div>
            <div>Total Deductions: {exactCurrencyFormatter(totalDeductions)}</div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div class="flex items-center gap-2">
          {skip_previous && (
            <Button
              variant="outline"
              onClick={() => setLocation(`/accounting/transaction/${skip_previous}`)}
              title="Previous transaction needing coding"
              class="h-8 w-8 p-0 border-0">
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          )}
          {previous && (
            <Button
              variant="outline"
              onClick={() => setLocation(`/accounting/transaction/${previous}`)}
              title="Previous transaction"
              class="h-8 w-8 p-0 border-0">
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
          <div class="text-center mx-2">
            <div class="">{totalTransactions} Transactions</div>
            {/*<div class="text-sm text-gray-400">*/}
            {/*  {codedCount}/{totalTransactions} coded*/}
            {/*</div>*/}
          </div>

          {next && (
            <Button
              variant="outline"
              onClick={() => setLocation(`/accounting/transaction/${next}`)}
              title="Next transaction"
              class="h-8 w-8 p-0">
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}

          {skip_next && (
            <Button
              variant="outline"
              onClick={() => setLocation(`/accounting/transaction/${skip_next}`)}
              title="Next transaction needing coding"
              class="h-8 w-8 p-0">
              <ChevronsRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {error}
    </div>
  )
}
